import React, { useEffect, useMemo, useState } from 'react'
import { Card, PageTitle, SearchField, StatusBadge, formatDate } from '../components/Ui'
import { adminRequest } from '../api/client'

function roleBadges(account) {
  const rows = []
  if (account.memberId) rows.push(<span className="account-role-badge is-member" key="member">개인회원</span>)
  if (account.directorStatus === 'APPROVED') rows.push(<span className="account-role-badge is-director" key="director">디렉터</span>)
  else if (account.directorStatus === 'SUSPENDED') rows.push(<span className="account-role-badge is-director" key="director-suspended">디렉터 정지</span>)
  else if (account.directorStatus === 'PENDING') rows.push(<span className="account-role-badge is-review" key="director-review">디렉터 신청</span>)
  else if (account.directorStatus === 'REJECTED') rows.push(<span className="account-role-badge is-review" key="director-rejected">디렉터 신청 거절</span>)
  return rows.length ? rows : <span className="account-role-badge">계정</span>
}

function directorState(account) {
  if (!account.directorId) return '-'
  if (account.directorStatus === 'APPROVED') return <StatusBadge status="APPROVED">승인</StatusBadge>
  if (account.directorStatus === 'SUSPENDED') return <StatusBadge status="SUSPENDED">이용 정지</StatusBadge>
  if (account.directorStatus === 'REJECTED') return <StatusBadge status="REJECTED">승인 거절</StatusBadge>
  return <StatusBadge status="PENDING">심사 대기</StatusBadge>
}

export default function AccountManagement({ navigate, initialRoleFilter = 'ALL' }) {
  const [items, setItems] = useState([])
  const [search, setSearch] = useState('')
  const [roleFilter, setRoleFilter] = useState(initialRoleFilter)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => { setRoleFilter(initialRoleFilter) }, [initialRoleFilter])

  useEffect(() => {
    let disposed = false
    adminRequest('/api/v1/admin/accounts')
      .then((response) => { if (!disposed) setItems(Array.isArray(response?.data) ? response.data : []) })
      .catch((reason) => { if (!disposed) setError(reason?.message || '회원 계정을 불러오지 못했습니다.') })
      .finally(() => { if (!disposed) setLoading(false) })
    return () => { disposed = true }
  }, [])

  const rows = useMemo(() => items.filter((item) => {
    const haystack = [item.name, item.nickname, item.email, item.phone, item.brandName, item.representativeName].filter(Boolean).join(' ').toLowerCase()
    const matchesSearch = !search || haystack.includes(search.toLowerCase())
    const matchesRole = roleFilter === 'ALL'
      || (roleFilter === 'MEMBER_ONLY' && item.memberId && !item.directorId)
      || (roleFilter === 'DIRECTOR' && ['APPROVED','SUSPENDED'].includes(item.directorStatus))
      || (roleFilter === 'APPLICATION' && item.directorStatus === 'PENDING')
      || (roleFilter === 'REJECTED' && item.directorStatus === 'REJECTED')
    return matchesSearch && matchesRole
  }), [items, search, roleFilter])

  const memberOnly = items.filter((item) => item.memberId && !item.directorId).length
  const directors = items.filter((item) => ['APPROVED','SUSPENDED'].includes(item.directorStatus)).length
  const pending = items.filter((item) => item.directorStatus === 'PENDING').length

  const openPrimary = (item) => {
    if (item.directorId && ['PENDING','REJECTED','SUSPENDED'].includes(item.directorStatus)) navigate(`/directors/${item.directorId}`)
    else if (item.memberId) navigate(`/members/${item.memberId}`)
    else if (item.directorId) navigate(`/directors/${item.directorId}`)
  }

  return <>
    <PageTitle eyebrow="ACCOUNTS" title="전체 회원" description="DIFAN 계정을 한 사람당 한 행으로 관리합니다. 개인회원이 디렉터 역할을 추가해도 별도 회원으로 중복 집계하지 않습니다." />
    <div className="metric-grid">
      <div className="metric-card metric-gold"><span>전체 계정</span><strong>{items.length.toLocaleString('ko-KR')}</strong><small>탈퇴 계정 제외</small></div>
      <div className="metric-card metric-blue"><span>개인회원만</span><strong>{memberOnly.toLocaleString('ko-KR')}</strong><small>디렉터 역할 없음</small></div>
      <div className="metric-card metric-green"><span>디렉터 회원</span><strong>{directors.toLocaleString('ko-KR')}</strong><small>승인·이용 정지 디렉터 역할 포함</small></div>
      <div className="metric-card metric-orange"><span>디렉터 심사 대기</span><strong>{pending.toLocaleString('ko-KR')}</strong><small>신청 후 승인 전</small></div>
    </div>

    {error ? <div className="admin-notice"><strong>오류</strong><span>{error}</span></div> : null}
    <Card className="table-card">
      <div className="table-toolbar">
        <SearchField value={search} onChange={setSearch} placeholder="이름, 닉네임, 이메일, 연락처, 디렉터명 검색" />
        <div className="filter-buttons account-role-filter">
          {[['ALL','전체'],['MEMBER_ONLY','개인회원만'],['DIRECTOR','디렉터 회원'],['APPLICATION','디렉터 신청 중'],['REJECTED','신청 거절']].map(([key, label]) => <button type="button" key={key} className={roleFilter === key ? 'active' : ''} onClick={() => setRoleFilter(key)}>{label}</button>)}
        </div>
      </div>
      <div className="table-result-bar"><span>검색 결과 <strong>{rows.length}</strong>계정</span><small>계정 1개 = 1행 · 역할은 배지로 구분합니다.</small></div>
      <div className="table-scroll"><table className="data-table"><thead><tr><th>회원</th><th>역할</th><th>연락처</th><th>디렉터 상태</th><th>사업자 심사</th><th>최근 로그인</th><th>계정 상태</th><th>관리</th></tr></thead><tbody>
        {loading ? <tr><td colSpan="8">회원 계정을 불러오는 중입니다.</td></tr> : rows.length ? rows.map((item) => <tr className="clickable-row" key={item.accountId} onClick={() => openPrimary(item)}>
          <td><strong>{item.name || item.brandName || '이름 미등록'}</strong><small>{item.nickname ? `@${item.nickname} · ` : ''}{item.email || '-'}</small></td>
          <td><div className="account-role-badges">{roleBadges(item)}</div></td>
          <td>{item.phone || '-'}</td>
          <td>{directorState(item)}</td>
          <td>{item.directorId ? <StatusBadge status={item.businessStatus || 'PENDING'} /> : '-'}</td>
          <td>{item.lastLoginAt ? formatDate(item.lastLoginAt, true) : '-'}</td>
          <td><StatusBadge status={item.memberStatus || item.accountStatus || 'ACTIVE'} /></td>
          <td><div className="row-action-buttons">
            {item.memberId ? <button type="button" className="text-button" onClick={(event) => { event.stopPropagation(); navigate(`/members/${item.memberId}`) }}>회원 정보</button> : null}
            {item.directorId ? <button type="button" className="text-button" onClick={(event) => { event.stopPropagation(); navigate(`/directors/${item.directorId}`) }}>디렉터 관리</button> : null}
          </div></td>
        </tr>) : <tr><td colSpan="8">조건에 맞는 회원이 없습니다.</td></tr>}
      </tbody></table></div>
    </Card>
  </>
}
